/**
 * Event chips («+ Досрочно каждый месяц» …) in `#events`. Each chip mirrors one checkbox
 * (`overpaymentEnabled`, `lumpEnabled`, `discountEnabled`, `refinanceEnabled`): clicking an
 * off chip checks the box, opens its `<details class="sheet">` and scrolls to it; clicking
 * an on chip (or the sheet's «Убрать» button) unchecks it. A sheet whose checkbox is off
 * is hidden by CSS (`.sheet-block:not(.sheet-enabled)`), so the form only shows the
 * events the user asked for.
 */

export const EVENT_TOGGLES = ['overpaymentEnabled', 'lumpEnabled', 'discountEnabled', 'refinanceEnabled'] as const;

export type EventToggle = (typeof EVENT_TOGGLES)[number];

const CHIP_LABELS: Record<EventToggle, string> = {
  overpaymentEnabled: 'Досрочно каждый месяц',
  lumpEnabled: 'Досрочно в выбранные месяцы',
  discountEnabled: 'Льготная ставка',
  refinanceEnabled: 'Рефинансирование',
};

const STRATEGY_GROUP_ID = 'strategy-group';

function isEventToggle(value: string | undefined): value is EventToggle {
  return (EVENT_TOGGLES as readonly string[]).includes(value ?? '');
}

/** Chip text: «+ Льготная ставка» while off, «✓ Льготная ставка» while on. */
export function chipLabel(name: EventToggle, on: boolean): string {
  return `${on ? '✓' : '+'} ${CHIP_LABELS[name]}`;
}

function checkboxOf(form: HTMLFormElement, name: EventToggle): HTMLInputElement | null {
  const control = form.elements.namedItem(name);
  return control instanceof HTMLInputElement ? control : null;
}

function blockOf(form: HTMLFormElement, name: EventToggle): HTMLElement | null {
  return form.querySelector<HTMLElement>(`.sheet-block[data-toggle="${name}"]`);
}

function setEnabled(form: HTMLFormElement, name: EventToggle, enabled: boolean): void {
  const checkbox = checkboxOf(form, name);
  if (!checkbox || checkbox.checked === enabled) return;
  checkbox.checked = enabled;
  // Bubbles to the form's change listener, which recomputes and re-syncs the chips.
  checkbox.dispatchEvent(new Event('change', { bubbles: true }));
}

function reveal(form: HTMLFormElement, name: EventToggle): void {
  const block = blockOf(form, name);
  if (!block) return;
  const sheet = block.querySelector<HTMLDetailsElement>('details.sheet');
  if (sheet) sheet.open = true;
  if (typeof block.scrollIntoView === 'function') {
    block.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * Reflects the checkboxes: chip state and text, `open` on enabled sheets, and the
 * strategy fieldset (it applies to monthly and one-off overpayments alike), shown only
 * while at least one of them is on. Safe to call on every recompute.
 */
export function syncEventChips(form: HTMLFormElement, root: ParentNode = document): void {
  EVENT_TOGGLES.forEach(name => {
    const on = checkboxOf(form, name)?.checked ?? false;
    root.querySelectorAll<HTMLButtonElement>(`.event-chip[data-event="${name}"]`).forEach(chip => {
      chip.classList.toggle('on', on);
      chip.setAttribute('aria-pressed', String(on));
      chip.textContent = chipLabel(name, on);
    });
    const sheet = blockOf(form, name)?.querySelector<HTMLDetailsElement>('details.sheet');
    if (sheet && on) sheet.open = true;
  });
  const strategy = root.querySelector<HTMLElement>(`#${STRATEGY_GROUP_ID}`);
  if (strategy) {
    const overpayments = (checkboxOf(form, 'overpaymentEnabled')?.checked ?? false)
      || (checkboxOf(form, 'lumpEnabled')?.checked ?? false);
    strategy.hidden = !overpayments;
  }
}

/** Binds the chips and every «Убрать» button; renders the initial chip state. */
export function initEventChips(form: HTMLFormElement, root: ParentNode = document): void {
  root.querySelectorAll<HTMLButtonElement>('.event-chip[data-event]').forEach(chip => {
    chip.addEventListener('click', () => {
      const name = chip.dataset.event;
      if (!isEventToggle(name)) return;
      const on = checkboxOf(form, name)?.checked ?? false;
      setEnabled(form, name, !on);
      if (!on) reveal(form, name);
      syncEventChips(form, root);
    });
  });
  form.querySelectorAll<HTMLButtonElement>('.sheet-remove[data-remove]').forEach(button => {
    button.addEventListener('click', () => {
      const name = button.dataset.remove;
      if (!isEventToggle(name)) return;
      setEnabled(form, name, false);
      syncEventChips(form, root);
    });
  });
  syncEventChips(form, root);
}
